import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, useNavigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import { useAuth } from './hooks/useAuth';

// Lazy-loaded route components for 0-lag initial bundle loading
const LoginPage = lazy(() => import('./components/auth/LoginPage'));
const DashboardLayout = lazy(() => import('./components/dashboard/DashboardLayout'));
const DashboardContent = lazy(() => import('./components/dashboard/DashboardContent'));
const WardManager = lazy(() => import('./components/wards/WardManager'));
const MemosPage = lazy(() => import('./components/memos/MemosPage'));
const UserActivityPage = lazy(() => import('./components/activity/UserActivityPage'));
const FeedbackAdminPage = lazy(() => import('./components/activity/FeedbackAdminPage'));
const MemoDetail = lazy(() => import('./components/memos/MemoDetail'));
const ThankYouPage = lazy(() => import('./components/auth/ThankYouPage'));
const Setting = lazy(() => import('./components/Setting'));

const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[60vh]">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
  </div>
);

const LogoutRoute = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  
  useEffect(() => {
    const performLogout = async () => {
      await logout();
      navigate('/thank-you', { replace: true });
    };
    performLogout();
  }, [logout, navigate]);

  return null; 
};

const AppRoutes = () => {
  const { isAuthenticated } = useAuth();

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="logout" element={<LogoutRoute />} />
        <Route path="thank-you" element={<ThankYouPage />} />
        {!isAuthenticated ? (
          <Route path="*" element={<LoginPage />} />
        ) : (
          <Route element={<DashboardLayout />}>
            <Route index element={<DashboardContent />} />
            <Route path="users" element={<DashboardContent activeTab="users" />} />
            <Route path="roles" element={<DashboardContent activeTab="roles" />} />
            <Route path="blocks" element={<DashboardContent activeTab="blocks" />} />
            <Route path="shifts" element={<DashboardContent activeTab="shifts" />} />
            <Route path="hierarchies" element={<DashboardContent activeTab="hierarchies" />} />
            <Route path="activity" element={<UserActivityPage />} />
            <Route path="feedbacks" element={<FeedbackAdminPage />} />
            <Route path="memos" element={<MemosPage />} />
            <Route path="settings" element={<Setting />} />
            <Route path="memo/:memoId" element={<MemoDetail />} />
            <Route path="wards/:blockId" element={<WardManager />} />
          </Route>
        )}
      </Routes>
    </Suspense>
  );
};




export default function MemoTrackApp() {
  return (
    <AuthProvider>
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <AppRoutes />
        <Toaster position="top-right" reverseOrder={false} />
      </BrowserRouter>
    </AuthProvider>
  );
}